import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { QcInspectionService } from '../../services/qcInspection.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { triggerDownload } from '../statutoryRegisters/shared';
import { formatDate } from '../../utils/dateUtils';
import { IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import {
    IQcFilters, IQcInspection, IQcSummary, QC_STATUS_LABELS, QC_TYPE_LABELS, QcInspectionStatus, QcInspectionType,
} from '../../interfaces/garments/qcInspection.interface';
import { QcBookDialog, QcReinspectDialog, QcResultDialog } from './QcInspectionDialogs';

const STATUS_COLOR: Record<QcInspectionStatus, 'default' | 'success' | 'error' | 'info'> = {
    SCHEDULED: 'info', PASSED: 'success', FAILED: 'error', CANCELLED: 'default',
};

/** Row actions, shared with the buyer-order QC panel. */
export interface QcRowActions {
    onResult: (q: IQcInspection) => void;
    onReinspect: (q: IQcInspection) => void;
    onEdit: (q: IQcInspection) => void;
    onCancel: (q: IQcInspection) => void;
    onPdf: (q: IQcInspection) => void;
}

export function QcRow({ q, canManage, actions }: { q: IQcInspection; canManage: boolean; actions: QcRowActions }) {
    return (
        <TableRow hover data-testid={`qc-row-${q.id}`}>
            <TableCell>{formatDate(q.inspectedDate ?? q.scheduledDate)}</TableCell>
            <TableCell>{q.orderNumber}</TableCell>
            <TableCell>{QC_TYPE_LABELS[q.inspectionType]}</TableCell>
            <TableCell align="right">{q.lotSize}</TableCell>
            <TableCell align="right">{q.fullInspection ? 'All' : q.sampleSize}</TableCell>
            <TableCell align="right">{q.criticalFound} / {q.majorFound} / {q.minorFound}</TableCell>
            <TableCell>
                <Chip size="small" color={STATUS_COLOR[q.status]} label={QC_STATUS_LABELS[q.status]} />
                {q.reinspectionOfId && <Chip size="small" variant="outlined" sx={{ ml: 0.5 }} label="Re-inspection" />}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {canManage && q.status === 'SCHEDULED' && (
                    <>
                        <Button size="small" data-help="merchandising.qc.recordResult" onClick={() => actions.onResult(q)}>Record result</Button>
                        <Button size="small" onClick={() => actions.onEdit(q)}>Edit</Button>
                        <Button size="small" color="warning" data-help="merchandising.qc.cancel" onClick={() => actions.onCancel(q)}>Cancel</Button>
                    </>
                )}
                {canManage && q.status === 'FAILED' && !q.reinspectionId && (
                    <Button size="small" data-help="merchandising.qc.reinspect" onClick={() => actions.onReinspect(q)}>Re-inspect</Button>
                )}
                {(q.status === 'PASSED' || q.status === 'FAILED') && (
                    <Button size="small" data-help="merchandising.qc.pdf" onClick={() => actions.onPdf(q)}>PDF</Button>
                )}
            </TableCell>
        </TableRow>
    );
}

/** Dialog state + handlers shared by the list page and the order panel. */
export function useQcActions(reload: () => void, notify: ReturnType<typeof useToast>) {
    const [booking, setBooking] = useState(false);
    const [editing, setEditing] = useState<IQcInspection | null>(null);
    const [resulting, setResulting] = useState<IQcInspection | null>(null);
    const [reinspecting, setReinspecting] = useState<IQcInspection | null>(null);

    const actions: QcRowActions = {
        onResult: setResulting,
        onReinspect: setReinspecting,
        onEdit: q => { setEditing(q); setBooking(true); },
        onCancel: async q => {
            const inst = AuthService.getSelectedInstitution();
            if (!inst || !window.confirm(`Cancel the ${QC_TYPE_LABELS[q.inspectionType]} inspection of ${q.orderNumber}?`)) return;
            try {
                await QcInspectionService.cancel(inst, q.id);
                notify.showSuccess('Inspection cancelled');
                reload();
            } catch (e) {
                notify.showApiError(e, 'Failed to cancel inspection');
            }
        },
        onPdf: async q => {
            const inst = AuthService.getSelectedInstitution();
            if (!inst) return;
            try {
                triggerDownload(await QcInspectionService.downloadPdf(inst, q.id), `qc-inspection-${q.orderNumber ?? q.id}.pdf`);
            } catch (e) {
                notify.showApiError(e, 'Failed to download the report');
            }
        },
    };

    const openBooking = () => { setEditing(null); setBooking(true); };
    const dialogs = (fixedOrderId?: string) => (
        <>
            <QcBookDialog open={booking} fixedOrderId={fixedOrderId} editing={editing}
                onClose={() => setBooking(false)} onSaved={reload} />
            <QcResultDialog inspection={resulting} onClose={() => setResulting(null)} onSaved={reload} />
            <QcReinspectDialog inspection={reinspecting} onClose={() => setReinspecting(null)} onSaved={reload} />
        </>
    );
    return { actions, openBooking, dialogs };
}

export const QC_TABLE_HEAD = (
    <TableHead>
        <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Order</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Lot</TableCell>
            <TableCell align="right">Sample</TableCell>
            <TableCell align="right" data-help="merchandising.qc.col.found">Critical / Major / Minor</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
        </TableRow>
    </TableHead>
);

export default function QcInspectionList() {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.QC_INSPECTION_MANAGE);

    const [rows, setRows] = useState<IQcInspection[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<IQcSummary | null>(null);
    const [orders, setOrders] = useState<IBuyerOrder[]>([]);

    const [orderId, setOrderId] = useState('');
    const [type, setType] = useState<QcInspectionType | ''>('');
    const [status, setStatus] = useState<QcInspectionStatus | ''>('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const load = useCallback(async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setLoading(true);
        const filters: IQcFilters = {
            buyerOrderId: orderId || undefined, type: type || undefined, status: status || undefined,
            from: from || undefined, to: to || undefined,
        };
        try {
            const [p, s] = await Promise.all([
                QcInspectionService.list(inst, filters, page, size),
                QcInspectionService.summary(inst, filters),
            ]);
            setRows(p.content);
            setTotal(p.totalElements);
            setSummary(s);
        } catch (e) {
            toast.showApiError(e, 'Failed to load inspections');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, type, status, from, to, page, size]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        BuyerOrderService.list(inst, 0, 200).then(p => setOrders(p.content)).catch(() => setOrders([]));
    }, []);

    const { actions, openBooking, dialogs } = useQcActions(load, toast);

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.qc.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Quality Inspections</Typography>
                {canManage && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openBooking} data-help="merchandising.qc.book">
                        Book inspection
                    </Button>
                )}
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.qc.kpi">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }} data-testid="qc-kpis">
                    <Box><Typography variant="caption">Pass rate</Typography>
                        <Typography variant="h6">{summary?.passRatePercent != null ? `${summary.passRatePercent}%` : '—'}</Typography></Box>
                    <Box><Typography variant="caption">Passed</Typography><Typography variant="h6">{summary?.passed ?? 0}</Typography></Box>
                    <Box><Typography variant="caption">Failed</Typography><Typography variant="h6">{summary?.failed ?? 0}</Typography></Box>
                    <Box><Typography variant="caption">Scheduled</Typography><Typography variant="h6">{summary?.scheduled ?? 0}</Typography></Box>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.qc.filter">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                        <SearchableSelect<IBuyerOrder> label="Order" size="small" options={orders} value={orderId}
                            getOptionLabel={o => o.orderNumber} onChange={id => { setOrderId(id); setPage(0); }} />
                    </Box>
                    <TextField select size="small" label="Type" value={type} sx={{ flex: '1 1 150px', maxWidth: 220 }}
                        onChange={e => { setType(e.target.value as QcInspectionType | ''); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        {(Object.keys(QC_TYPE_LABELS) as QcInspectionType[]).map(t => <MenuItem key={t} value={t}>{QC_TYPE_LABELS[t]}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Status" value={status} sx={{ flex: '1 1 150px', maxWidth: 220 }}
                        onChange={e => { setStatus(e.target.value as QcInspectionStatus | ''); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        {(Object.keys(QC_STATUS_LABELS) as QcInspectionStatus[]).map(s => <MenuItem key={s} value={s}>{QC_STATUS_LABELS[s]}</MenuItem>)}
                    </TextField>
                    <TextField type="date" size="small" label="From" value={from} InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 150px', maxWidth: 200 }} onChange={e => { setFrom(e.target.value); setPage(0); }} />
                    <TextField type="date" size="small" label="To" value={to} InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 150px', maxWidth: 200 }} onChange={e => { setTo(e.target.value); setPage(0); }} />
                </Box>
            </Paper>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        {QC_TABLE_HEAD}
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={8} align="center">No inspections found.</TableCell></TableRow>
                            )}
                            {rows.map(q => <QcRow key={q.id} q={q} canManage={canManage} actions={actions} />)}
                        </TableBody>
                    </Table>
                    <TablePagination component="div" count={total} page={page} rowsPerPage={size}
                        rowsPerPageOptions={[10, 20, 50]} onPageChange={(_, p) => setPage(p)}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }} />
                </TableContainer>
            )}
            {dialogs()}
        </Box>
    );
}
